import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import useManageIssuedChangeDecision from './_data_sources/useManageIssuedChangeDecision';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import { enqueueSnackbar } from 'notistack';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import {
  DraftManual,
  DraftPolicy,
  DraftPreface,
  DraftPolicySection,
  DraftPolicySubSection,
  PolicyUpdate,
  PrefaceUpdate,
  SectionUpdate,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useManagePreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePreface';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import usePendingUpdates from 'views/App/Portal/Policy/DraftManuals/_data_sources/usePendingUpdates';
import _ from 'lodash';
import ContentConversionHelper from '../../helpers/contentConversionHelper';

export enum State {
  NO_UPDATES = 'NO_UPDATES',
  UPDATES = 'UPDATES',
  UPDATES_CHANGING = 'UPDATES_CHANGING',
}

export interface PendingUpdateResponse {
  id: string;
  policyId: string;
  sections?: PendingUpdateSection[];
  changes?: Array<{
    change: string;
    sectionEdit: boolean;
    subsectionEdit: boolean;
    policyEdit: boolean;
    stateChange: boolean;
  }>;
  createdAt: string;
  title: string;
}

export interface PendingUpdateSection {
  id: string;
  state?: string;
  title?: string;
  changes?: Array<{ change: string }>;
  content?: string;
  action?: string;
  subSections?: PendingUpdateSubSection[];
}

export interface PendingUpdateSubSection {
  id: string;
  state?: string;
  title?: string;
  content?: string;
  changes?: Array<{ change: string }>;
  action?: string;
  contentAction?: string;
}

export interface ReviewSection {
  id: string;
  title: string;
  content?: string;
  date: string;
}

export interface PendingUpdateItem {
  id: string;
  index?: string;
  agencyContent: ReviewSection;
  lexipolUpdate?: ReviewSection;
  isPending: boolean;
  state: State | string;
  isPolicyTitle?: boolean;
  isSubsection?: boolean;
}

interface SidebarItem {
  id: string;
  title: string;
  changeDate: string;
}

interface ReviewUpdatesContextProps {
  focus: string | null;
  isLoading: boolean;
  setFocus: (newIndex: string | null) => void;
  handleAcceptRecommendations: (id: string) => void;
  handleRetainCurrent: (id: string) => void;
  handleSaveCustomizations: (id: string, newContent: string, editorTitle: string) => void;
  handleDone: () => Promise<void>;
  manual: DraftManual | null;
  policyUpdates?: PolicyUpdate | null;
  policyQuery: any;
  prefaceUpdates?: PrefaceUpdate | null;
  prefaceQuery: any;
  manualQuery: any;
  pendingUpdatesQuery: any;
  policyData: DraftPolicy | null | undefined;
  prefaceData: DraftPreface | null | undefined;
  sidebarItems: SidebarItem[];
  policyId: string;
  prefaceId: string;
  isRightDrawerOpen: boolean;
  setIsRightDrawerOpen: (open: boolean) => void;
}
const ReviewUpdatesContext = createContext<ReviewUpdatesContextProps>({
  focus: null,
  isLoading: false,
  setFocus: () => undefined,
  manual: null,
  policyUpdates: null,
  prefaceUpdates: null,
  sidebarItems: [],
  policyQuery: null,
  prefaceQuery: null,
  manualQuery: null,
  pendingUpdatesQuery: null,
  policyData: null,
  prefaceData: null,
  handleAcceptRecommendations: () => undefined,
  handleRetainCurrent: () => undefined,
  handleSaveCustomizations: () => undefined,
  handleDone: async () => undefined,
  policyId: '',
  prefaceId: '',
  isRightDrawerOpen: true,
  setIsRightDrawerOpen: () => undefined,
});

export const ReviewUpdatesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { id: manualId, content: contentParam } = useParams<{
    id: string;
    content: string;
  }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  let prefaceId = '';
  let policyId = '';
  if (contentParam) {
    if (contentParam.startsWith('preface:')) {
      prefaceId = contentParam.replace('preface:', '');
    } else if (contentParam.startsWith('policy:')) {
      policyId = contentParam.replace('policy:', '');
    }
  }
  const [focus, setFocus] = useState<string | null>(null);
  const [isRightDrawerOpen, setIsRightDrawerOpen] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { processIssuedPolicyChangeDecision, processIssuedPrefaceChangeDecision } = useManageIssuedChangeDecision();
  const { update: updatePolicy, removeLock: removePolicyLock } = useManagePolicy();

  const policyQuery = useDraftPolicy(manualId, policyId);
  const prefaceQuery = useDraftPreface(manualId, prefaceId);
  const { update: updatePreface, removeLock: removePrefaceLock } = useManagePreface();
  const manualQuery = useDraftManual(manualId);
  const [policyData, setPolicyData] = useState<DraftPolicy | null>();
  const [prefaceData, setPrefaceData] = useState<DraftPreface | null>();
  const [manual, setManual] = useState<DraftManual | null>();
  const [policyUpdates, setPolicyUpdates] = useState<PolicyUpdate | null>();
  const [prefaceUpdates, setPrefaceUpdates] = useState<PrefaceUpdate | null>();
  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    if (policyQuery.data) setPolicyData(policyQuery.data);
  }, [policyQuery.data]);

  useEffect(() => {
    if (prefaceQuery.data) setPrefaceData(prefaceQuery.data);
  }, [prefaceQuery.data]);

  useEffect(() => {
    if (manualQuery.data) setManual(manualQuery.data);
  }, [manualQuery.data]);

  const pendingUpdatesQuery = usePendingUpdates(manual, {
    enabled:
      !manualQuery.isLoading &&
      !manualQuery.isFetching &&
      !policyQuery.isLoading &&
      !policyQuery.isFetching &&
      !prefaceQuery.isLoading &&
      !prefaceQuery.isFetching,
  });

  useEffect(() => {
    if (!pendingUpdatesQuery.isLoading && !pendingUpdatesQuery.isFetching) {
      if (policyId && pendingUpdatesQuery.data) {
        setPolicyUpdates(pendingUpdatesQuery.data?.policies[policyId] || null);
      }
      if (prefaceId && pendingUpdatesQuery.data) {
        setPrefaceUpdates(pendingUpdatesQuery.data?.prefaces[prefaceId] || null);
      }
      const items: SidebarItem[] = [];
      if (
        !_.isEmpty(pendingUpdatesQuery.data?.policies[policyId]) ||
        !_.isEmpty(pendingUpdatesQuery.data?.prefaces[prefaceId])
      ) {
        if (!_.isEmpty(pendingUpdatesQuery.data?.policies[policyId])) {
          if (pendingUpdatesQuery.data?.policies[policyId].policyTitleUpdate) {
            items.push({
              id: 'policy-title',
              title: [
                pendingUpdatesQuery.data?.policies[policyId].policyNumber,
                pendingUpdatesQuery.data?.policies[policyId].updatedTitle,
              ].join(': '),
              changeDate: pendingUpdatesQuery.data?.policies[policyId].changeDate,
            });
          }
          pendingUpdatesQuery.data?.policies[policyId].sections.forEach((section) => {
            items.push({
              id: section.id,
              title: [section.sectionNumber, section.isNew ? section.currentTitle || '' : section.updatedTitle].join(
                ': '
              ),
              changeDate: section.isNew ? section.updatedDate || '' : section.changeDate,
            });
          });
        }
        if (!_.isEmpty(pendingUpdatesQuery.data?.prefaces[prefaceId])) {
          if (pendingUpdatesQuery.data?.prefaces[prefaceId]) {
            items.push({
              id: 'preface',
              title: pendingUpdatesQuery.data?.prefaces[prefaceId].updatedTitle,
              changeDate: pendingUpdatesQuery.data?.prefaces[prefaceId].changeDate,
            });
          }
        }
      }
      setSidebarItems(items);
      if (!focus) {
        if (!_.isEmpty(items) && items?.length > 0) {
          setFocus(items[0].id);
        }
      }
    }
  }, [
    focus,
    policyId,
    prefaceId,
    pendingUpdatesQuery.data,
    pendingUpdatesQuery.isLoading,
    pendingUpdatesQuery.isFetching,
    manualQuery.data,
    manualQuery.isFetching,
    setPolicyUpdates,
    setPrefaceUpdates,
    setSidebarItems,
  ]);

  const updatePrefaceContent = async (prefaceUpdate: PrefaceUpdate) => {
    const updatedPreface: DraftPreface = {
      ...prefaceData!,
      content: ContentConversionHelper.contentForRender(prefaceUpdate.updatedContent),
      contentEditable: ContentConversionHelper.contentForEditor(prefaceUpdate.updatedContent),
      title: prefaceUpdate.updatedTitle,
      attachments: prefaceUpdate.updatedAttachments,
    };
    await updatePreface(
      manualId!,
      prefaceId,
      updatedPreface,
      () => {},
      (errors: ServerErrorResponse) => {
        errorHelper.enqueueErrors(errors);
        throw new Error('Failed to update preface');
      }
    );
  };

  const updatePolicyTitle = async (newTitle: string) => {
    const updatedPolicy: DraftPolicy = {
      ...policyData!,
      title: newTitle,
    };
    await updatePolicy(
      manualId!,
      policyId,
      updatedPolicy,
      () => {},
      (errors: ServerErrorResponse) => {
        errorHelper.enqueueErrors(errors);
        throw new Error('Failed to update policy title');
      }
    );
  };

  const ensureEditLevelTags = (
    subsections: DraftPolicySubSection[],
    parentTag: 'FEDERAL' | 'STATE' | 'BEST_PRACTICE' | 'DISCRETIONARY' | 'AGENCY_CONTENT' | 'ACCREDITATION'
  ) => {
    if (!subsections || !Array.isArray(subsections)) return;

    subsections.forEach((sub) => {
      if (!sub.editLevelTag) {
        sub.editLevelTag = parentTag;
      }
    });
  };

  const updatePolicySection = async (section: SectionUpdate) => {
    const updatedPolicy: DraftPolicy = {
      ...policyData!,
    };

    updatedPolicy.sections.forEach((s: DraftPolicySection) => {
      if (s.editLevelTag && s.subSections && s.subSections.length > 0) {
        ensureEditLevelTags(s.subSections, s.editLevelTag);
      }
    });

    updatedPolicy.sections.forEach((s: DraftPolicySection) => {
      if (s.id === section.id) {
        s.contentEditable = ContentConversionHelper.contentForEditor(section.updatedContent);
        s.content = section.updatedContent;
        s.attachments = section.updatedAttachments;
        if (section.sectionTitleUpdate) {
          s.title = section.updatedTitle;
        }
        if (section.stateChange) {
          s.state = section.updatedState as 'ENABLED' | 'DISABLED';
        }
        if (section.isNew) {
          s.isNew = false;
          if (section.stateChange) {
            s.state = section.updatedState as 'ENABLED' | 'DISABLED';
          } else {
            s.state = 'ENABLED';
          }
          if (s.editLevelTag && s.subSections && s.subSections.length > 0) {
            ensureEditLevelTags(s.subSections, s.editLevelTag);
          }
        }
      } else {
        s.subSections.forEach((ss: DraftPolicySubSection) => {
          if (ss.id === section.id) {
            ss.contentEditable = ContentConversionHelper.contentForEditor(section.updatedContent);
            ss.content = section.updatedContent;
            ss.attachments = section.updatedAttachments;
            if (section.sectionTitleUpdate) {
              ss.title = section.updatedTitle;
            }
            if (section.stateChange) {
              ss.state = section.updatedState as 'ENABLED' | 'DISABLED';
            }
            if (section.isNew) {
              ss.isNew = false;
              if (section.stateChange) {
                ss.state = section.updatedState as 'ENABLED' | 'DISABLED';
              } else {
                ss.state = 'ENABLED';
              }
            }
          }
        });
      }
    });

    await updatePolicy(
      manualId!,
      policyId,
      updatedPolicy,
      () => {},
      (errors: ServerErrorResponse) => {
        errorHelper.enqueueErrors(errors);
        throw new Error('Failed to update policy section');
      }
    );
  };

  const handleAcceptRecommendations = async (id: string) => {
    if (!manualId) return;
    setIsLoading(true);
    try {
      if (policyUpdates) {
        if (id === 'policy-title' && policyUpdates?.titleChangesetId) {
          try {
            await updatePolicyTitle(policyUpdates.updatedTitle);
            await processIssuedPolicyChangeDecision(manualId, policyUpdates.titleChangesetId!, 'ACCEPT', () => {});
            await policyQuery.refetch();
            await pendingUpdatesQuery.refetch();
            const item = sidebarItems.find((x) => x.id === focus);
            const currentIndex = _.indexOf(sidebarItems, item);
            if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
              setFocus(sidebarItems[currentIndex + 1].id);
            } else {
              setFocus(null);
            }
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error updating policy title: ', error);
            enqueueSnackbar('Failed to update policy title', { variant: 'error' });
            throw error;
          }
        } else {
          const section = policyUpdates.sections.find((s) => s.id === id);
          if (section) {
            try {
              await updatePolicySection(section);
              await Promise.all(
                _.uniq(section.changesetIds).map((x) => processIssuedPolicyChangeDecision(manualId, x, 'ACCEPT'))
              );
              await policyQuery.refetch();
              await pendingUpdatesQuery.refetch();
              const item = sidebarItems.find((x) => x.id === focus);
              const currentIndex = _.indexOf(sidebarItems, item);
              if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
                setFocus(sidebarItems[currentIndex + 1].id);
              } else {
                setFocus(null);
              }
              queryClient.invalidateQueries(['draft_manual', manualId]);
              enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
              console.log('Section update successful:', section.id);
            } catch (error: any) {
              console.error('Error updating policy section: ', error);
              if (error.response && error.response.data) {
                console.error('Error details:', JSON.stringify(error.response.data, null, 2));
              }
              enqueueSnackbar('Failed to update policy section', { variant: 'error' });
              throw error;
            }
          }
        }
      }
      if (prefaceUpdates) {
        if (id === 'preface') {
          try {
            await updatePrefaceContent(prefaceUpdates);
            await processIssuedPrefaceChangeDecision(manualId, prefaceUpdates.changesetId!, 'ACCEPT', () => {});
            await prefaceQuery.refetch();
            await pendingUpdatesQuery.refetch();
            setFocus(null);
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error updating preface: ', error);
            enqueueSnackbar('Failed to update preface', { variant: 'error' });
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error in handleAcceptRecommendations: ', error);
      enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetainCurrent = async (id: string) => {
    if (!manualId) return;
    setIsLoading(true);
    try {
      if (policyUpdates) {
        if (id === 'policy-title' && policyUpdates?.titleChangesetId) {
          try {
            if (policyData) {
              await updatePolicy(
                manualId!,
                policyId,
                policyData,
                () => {},
                (errors: ServerErrorResponse) => {
                  errorHelper.enqueueErrors(errors);
                  throw new Error('Failed to update policy');
                }
              );
            }
            await processIssuedPolicyChangeDecision(manualId, policyUpdates.titleChangesetId!, 'REJECT', () => {});
            await policyQuery.refetch();
            await pendingUpdatesQuery.refetch();
            const item = sidebarItems.find((x) => x.id === focus);
            const currentIndex = _.indexOf(sidebarItems, item);
            if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
              setFocus(sidebarItems[currentIndex + 1].id);
            } else {
              setFocus(null);
            }
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Rejected Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error rejecting policy title: ', error);
            enqueueSnackbar('Failed to reject policy title', { variant: 'error' });
            throw error;
          }
        } else {
          const section = policyUpdates.sections.find((s) => s.id === id);
          if (section) {
            try {
              if (policyData) {
                const updatedPolicy = { ...policyData };
                if (section.isNew) {
                  updatedPolicy.sections = updatedPolicy.sections.map((s) => {
                    if (s.id === id) {
                      return { ...s, isNew: false };
                    }
                    return s;
                  });
                }
                await updatePolicy(
                  manualId!,
                  policyId,
                  updatedPolicy,
                  () => {},
                  (errors: ServerErrorResponse) => {
                    errorHelper.enqueueErrors(errors);
                    throw new Error('Failed to update policy');
                  }
                );
              }
              await Promise.all(
                _.uniq(section.changesetIds).map((x) => processIssuedPolicyChangeDecision(manualId, x, 'REJECT'))
              );
              await policyQuery.refetch();
              await pendingUpdatesQuery.refetch();
              const item = sidebarItems.find((x) => x.id === focus);
              const currentIndex = _.indexOf(sidebarItems, item);
              if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
                setFocus(sidebarItems[currentIndex + 1].id);
              } else {
                setFocus(null);
              }
              queryClient.invalidateQueries(['draft_manual', manualId]);
              enqueueSnackbar('Content Rejected Successfully', { variant: 'success' });
            } catch (error) {
              console.error('Error rejecting policy section: ', error);
              enqueueSnackbar('Failed to reject policy section', { variant: 'error' });
              throw error;
            }
          }
        }
      }
      if (prefaceUpdates) {
        if (id === 'preface') {
          try {
            if (prefaceData) {
              const currentPrefaceUpdate: PrefaceUpdate = {
                count: 0,
                severity: '',
                changeDate: '',
                prefaceTitleUpdate: false,
                prefaceContentUpdate: false,
                currentTitle: prefaceData.title,
                updatedTitle: prefaceData.title,
                currentContent: prefaceData.content,
                updatedContent: prefaceData.content,
                currentAttachments: prefaceData.attachments || [],
                updatedAttachments: prefaceData.attachments || [],
              };
              await updatePrefaceContent(currentPrefaceUpdate);
            }
            await processIssuedPrefaceChangeDecision(manualId, prefaceUpdates.changesetId!, 'REJECT', () => {});
            await prefaceQuery.refetch();
            await pendingUpdatesQuery.refetch();
            setFocus(null);
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Rejected Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error rejecting preface: ', error);
            enqueueSnackbar('Failed to reject preface', { variant: 'error' });
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error in handleRetainCurrent: ', error);
      enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCustomizations = async (id: string, newContent: string, editorTitle: string) => {
    if (!manualId) return;
    setIsLoading(true);
    try {
      if (policyUpdates) {
        if (id === 'policy-title' && policyUpdates?.titleChangesetId) {
          try {
            await updatePolicyTitle(editorTitle);
            await processIssuedPolicyChangeDecision(manualId, policyUpdates.titleChangesetId!, 'REJECT', () => {});
            await policyQuery.refetch();
            await pendingUpdatesQuery.refetch();
            const item = sidebarItems.find((x) => x.id === focus);
            const currentIndex = _.indexOf(sidebarItems, item);
            if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
              setFocus(sidebarItems[currentIndex + 1].id);
            } else {
              setFocus(null);
            }
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error saving policy title customization: ', error);
            enqueueSnackbar('Failed to save policy title customization', { variant: 'error' });
            throw error;
          }
        } else {
          const section = policyUpdates.sections.find((s) => s.id === id);
          if (section) {
            try {
              const updatedSection = {
                ...section,
                updatedTitle: editorTitle,
                updatedContent: newContent,
              };
              await updatePolicySection(updatedSection);
              await Promise.all(
                _.uniq(section.changesetIds).map((x) => processIssuedPolicyChangeDecision(manualId, x, 'REJECT'))
              );
              await policyQuery.refetch();
              await pendingUpdatesQuery.refetch();
              const item = sidebarItems.find((x) => x.id === focus);
              const currentIndex = _.indexOf(sidebarItems, item);
              if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
                setFocus(sidebarItems[currentIndex + 1].id);
              } else {
                setFocus(null);
              }
              queryClient.invalidateQueries(['draft_manual', manualId]);
              enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
            } catch (error) {
              console.error('Error saving policy section customization: ', error);
              enqueueSnackbar('Failed to save policy section customization', { variant: 'error' });
              throw error;
            }
          }
        }
      }
      if (prefaceUpdates) {
        if (id === 'preface') {
          try {
            if (prefaceData) {
              const customPrefaceUpdate: PrefaceUpdate = {
                count: 0,
                severity: '',
                changeDate: '',
                prefaceTitleUpdate: false,
                prefaceContentUpdate: false,
                currentTitle: prefaceData.title,
                updatedTitle: editorTitle,
                currentContent: prefaceData.content,
                updatedContent: newContent,
                currentAttachments: prefaceData.attachments || [],
                updatedAttachments: prefaceData.attachments || [],
              };
              await updatePrefaceContent(customPrefaceUpdate);
            }
            await processIssuedPrefaceChangeDecision(manualId, prefaceUpdates.changesetId!, 'REJECT', () => {});
            await prefaceQuery.refetch();
            await pendingUpdatesQuery.refetch();
            setFocus(null);
            queryClient.invalidateQueries(['draft_manual', manualId]);
            enqueueSnackbar('Content Updated Successfully', { variant: 'success' });
          } catch (error) {
            console.error('Error saving preface customization: ', error);
            enqueueSnackbar('Failed to save preface customization', { variant: 'error' });
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error in handleSaveCustomizations: ', error);
      enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDone = async () => {
    setIsLoading(true);
    if (manualId) {
      if (policyId) {
        await removePolicyLock(manualId, policyId, () => {
          queryClient.invalidateQueries(['draft_manuals']);
          queryClient.invalidateQueries(['draft_manual', manualId]);
          queryClient.invalidateQueries(['draft_policy', manualId, policyId]);
        });
      } else if (prefaceId) {
        await removePrefaceLock(manualId, prefaceId, () => {
          queryClient.invalidateQueries(['draft_manuals']);
          queryClient.invalidateQueries(['draft_manual', manualId]);
          queryClient.invalidateQueries(['draft_preface', manualId, prefaceId]);
        });
      }
      setIsLoading(false);
      if (policyId) {
        navigate(`../${manualId}/policy:${policyId}`);
      } else if (prefaceId) {
        navigate(`../${manualId}/preface:${prefaceId}`);
      } else {
        navigate(`../${manualId}`);
      }
    }
  };

  return (
    <ReviewUpdatesContext.Provider
      value={{
        focus,
        setFocus,
        isLoading,
        manual: manual || null,
        policyUpdates,
        prefaceUpdates,
        sidebarItems,
        policyQuery,
        prefaceQuery,
        manualQuery,
        pendingUpdatesQuery,
        policyData,
        prefaceData,
        handleAcceptRecommendations,
        handleRetainCurrent,
        handleSaveCustomizations,
        handleDone,
        prefaceId,
        policyId,
        isRightDrawerOpen,
        setIsRightDrawerOpen,
      }}
    >
      {children}
    </ReviewUpdatesContext.Provider>
  );
};
export const useReviewUpdatesContext = () => useContext(ReviewUpdatesContext);
